import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import FamilyWorshipBulkUpload from './FamilyWorshipBulkUpload';

const PAGE_SIZE = 50;

/** type="month" 값 "YYYY-MM" → 해당 월 첫날·마지막날 YYYYMMDD */
function monthInputToDevotionsRange(monthStr) {
  if (!monthStr || typeof monthStr !== 'string') return null;
  const parts = monthStr.split('-');
  if (parts.length !== 2) return null;
  const y = Number(parts[0], 10);
  const m = Number(parts[1], 10);
  if (!y || !m || m < 1 || m > 12) return null;
  const ym = `${String(y)}${String(m).padStart(2, '0')}`;
  const lastDay = new Date(y, m, 0).getDate();
  return {
    from: `${ym}01`,
    to: `${ym}${String(lastDay).padStart(2, '0')}`,
  };
}

function sanitizeTextSearch(raw) {
  return raw.trim().replace(/[%_,]/g, '');
}

function getCurrentMonthInput() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

const FamilyWorshipList = () => {
  const [devotions, setDevotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const observerTarget = useRef(null);

  const [searchMonth, setSearchMonth] = useState(() => getCurrentMonthInput());
  const [searchText, setSearchText] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [currentFilters, setCurrentFilters] = useState({
    monthRaw: '',
    textRaw: '',
    langRaw: ''
  });

  const buildQuery = (monthRaw, textRaw, langRaw) => {
    let query = supabase.from('family_devotions').select('*', { count: 'exact' });

    const range = monthInputToDevotionsRange(monthRaw);
    if (range) {
      query = query.gte('devotions_date', range.from).lte('devotions_date', range.to);
    }

    const lang = typeof langRaw === 'string' ? langRaw.trim() : '';
    if (lang) {
      query = query.eq('language', lang);
    }

    const text = sanitizeTextSearch(textRaw);
    if (text) {
      const p = `%${text}%`;
      query = query.or(
        `title.ilike.${p},hymn.ilike.${p},grace_sharing_text.ilike.${p},scripture_reference.ilike.${p},scripture_text.ilike.${p},closing_text.ilike.${p},commentary_text.ilike.${p},lets_get_it_text.ilike.${p},etc1.ilike.${p},devotions_date.ilike.${p}`
      );
    }

    return query.order('devotions_date', { ascending: false });
  };

  const runFetch = async (monthRaw, textRaw, langRaw) => {
    setLoading(true);
    setError(null);
    setOffset(0);
    setHasMore(true);
    setDevotions([]);
    setCurrentFilters({ monthRaw, textRaw, langRaw });

    try {
      const query = buildQuery(monthRaw, textRaw, langRaw);
      const { data, error: fetchError } = await query.range(0, PAGE_SIZE - 1);

      if (fetchError) throw fetchError;

      setDevotions(data || []);
      setOffset(PAGE_SIZE);
      setHasMore((data?.length || 0) >= PAGE_SIZE);
    } catch (e) {
      console.error(e);
      setError(e.message || '목록 조회에 실패했습니다.');
      setDevotions([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const query = buildQuery(currentFilters.monthRaw, currentFilters.textRaw, currentFilters.langRaw);
      const { data, error: fetchError } = await query.range(offset, offset + PAGE_SIZE - 1);

      if (fetchError) throw fetchError;

      const newData = data || [];
      setDevotions(prev => [...prev, ...newData]);
      setOffset(prev => prev + PAGE_SIZE);
      setHasMore(newData.length >= PAGE_SIZE);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    runFetch(searchMonth, '', '');
  }, []);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loadingMore, loading, offset, currentFilters]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    runFetch(searchMonth, searchText, languageFilter);
  };

  const handleResetSearch = () => {
    setSearchMonth('');
    setSearchText('');
    setLanguageFilter('');
    runFetch('', '', '');
  };

  const handleRowClick = (id, event) => {
    if (event && event.target.type === 'checkbox') {
      return;
    }
    navigate(`/family-worship/${id}`);
  };

  const handleCheckboxChange = (id, event) => {
    event.stopPropagation();
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(devotions.map((item) => item.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleDelete = async () => {
    if (!selectedIds.length) {
      alert('삭제할 항목을 선택해주세요.');
      return;
    }

    if (
      !window.confirm(
        `선택한 ${selectedIds.length}개의 가정예배를 삭제하시겠습니까?\n관련된 질문(family_devotions_q)도 함께 삭제됩니다.`
      )
    ) {
      return;
    }

    setDeleting(true);
    try {
      const { error } = await supabase.from('family_devotions').delete().in('id', selectedIds);

      if (error) throw error;

      alert('삭제가 완료되었습니다.');
      setSelectedIds([]);
      await runFetch(searchMonth, searchText, languageFilter);
    } catch (err) {
      console.error('삭제 오류:', err);
      alert(`삭제 중 오류가 발생했습니다: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleActiveChange = async (e, id, currentStatus) => {
    e.stopPropagation();
    const newStatus = currentStatus === 'Y' ? 'N' : 'Y';
    const { error: updError } = await supabase
      .from('family_devotions')
      .update({ is_active: newStatus })
      .eq('id', id);

    if (updError) {
      setError(updError.message);
    } else {
      await runFetch(searchMonth, searchText, languageFilter);
    }
  };

  const handleCreate = () => {
    navigate('/family-worship/new');
  };

  const handleBulkUpload = () => {
    setShowBulkUpload(true);
  };

  const handleBulkUploadClose = () => {
    setShowBulkUpload(false);
    runFetch(searchMonth, searchText, languageFilter);
  };

  return (
    <>
      {showBulkUpload && <FamilyWorshipBulkUpload onClose={handleBulkUploadClose} />}
      <div className="card shadow mb-4">
        <div className="card-header py-3 d-flex justify-content-between align-items-center">
          <h6 className="m-0 font-weight-bold text-primary">가정예배 목록</h6>
          <div>
            <button className="btn btn-success btn-sm mr-2" onClick={handleBulkUpload}>
              일괄입력
            </button>
            <button
              className="btn btn-danger btn-sm mr-2"
              onClick={handleDelete}
              disabled={!selectedIds.length || deleting || loading}
            >
              {deleting ? '삭제 중...' : '삭제'}
            </button>
            <button className="btn btn-primary btn-sm" onClick={handleCreate}>
              새 가정예배
            </button>
          </div>
        </div>
        <div className="card-body">
          {error && <div className="alert alert-danger">{error}</div>}

          <form className="mb-4 p-3 bg-light rounded border" onSubmit={handleSearchSubmit}>
            <div className="form-row align-items-end">
              <div className="form-group col-md-3 col-lg-2 mb-2 mb-md-0">
                <label htmlFor="fw-search-month" className="small font-weight-bold mb-1">
                  검색 월
                </label>
                <input
                  id="fw-search-month"
                  type="month"
                  className="form-control form-control-sm"
                  value={searchMonth}
                  onChange={(e) => setSearchMonth(e.target.value)}
                />
              </div>
              <div className="form-group col-md-3 col-lg-2 mb-2 mb-md-0">
                <label htmlFor="fw-language" className="small font-weight-bold mb-1">
                  언어 (language)
                </label>
                <input
                  id="fw-language"
                  type="text"
                  className="form-control form-control-sm"
                  list="fw-language-list"
                  placeholder="전체"
                  value={languageFilter}
                  onChange={(e) => setLanguageFilter(e.target.value)}
                  autoComplete="off"
                />
                <datalist id="fw-language-list">
                  <option value="ko" />
                  <option value="en" />
                  <option value="ja" />
                  <option value="zh" />
                </datalist>
              </div>
              <div className="form-group col-md-6 col-lg-4 mb-2 mb-md-0">
                <label htmlFor="fw-text-search" className="small font-weight-bold mb-1">
                  검색 단어
                </label>
                <input
                  id="fw-text-search"
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="제목, 부제, 찬송, 말씀 본문 등"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
              <div className="form-group col-md-12 col-lg-4 mb-0 d-flex flex-wrap align-items-end" style={{ gap: 8 }}>
                <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
                  검색
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleResetSearch}
                  disabled={loading}
                >
                  조건 초기화
                </button>
              </div>
            </div>
            <small className="text-muted d-block mt-2 mb-0">
              검색 월은 <code>devotions_date</code>(YYYYMMDD)가 해당 월에 속하는 행만 표시합니다. 언어는 DB{' '}
              <code>language</code> 컬럼과 정확히 일치합니다. 검색 단어는 여러 텍스트 필드에서 부분 일치(대소문자 무시)합니다.
            </small>
          </form>

          {loading ? (
            <div className="text-muted py-3">조회 중...</div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead className="thead-light">
                    <tr>
                      <th style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.length === devotions.length && devotions.length > 0}
                          onChange={(event) => handleSelectAll(event.target.checked)}
                        />
                      </th>
                      <th>진행일</th>
                      <th>제목</th>
                      <th>언어</th>
                      <th>찬송</th>
                      <th>말씀 본문</th>
                      <th>사용</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devotions.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center text-muted">
                          등록된 가정예배가 없습니다.
                        </td>
                      </tr>
                    ) : (
                      devotions.map((devotion) => (
                        <tr
                          key={devotion.id}
                          style={{ cursor: 'pointer' }}
                          onClick={(e) => handleRowClick(devotion.id, e)}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(devotion.id)}
                              onChange={(e) => handleCheckboxChange(devotion.id, e)}
                            />
                          </td>
                          <td>{formatDisplayDate(devotion.devotions_date)}</td>
                          <td>{devotion.title}</td>
                          <td>
                            {devotion.language ||
                              (devotion.country_cd === 'US'
                                ? 'en'
                                : devotion.country_cd === 'KR'
                                  ? 'ko'
                                  : devotion.country_cd || '-')}
                          </td>
                          <td>{devotion.hymn || '-'}</td>
                          <td>{devotion.scripture_reference}</td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={devotion.is_active === 'Y'}
                              onChange={(e) => handleActiveChange(e, devotion.id, devotion.is_active)}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* 스크롤 페이징 트리거 */}
              <div ref={observerTarget} className="text-center py-4">
                {loadingMore && (
                  <div className="text-muted">
                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    더 불러오는 중...
                  </div>
                )}
                {!hasMore && devotions.length > 0 && (
                  <small className="text-muted">모든 데이터를 불러왔습니다.</small>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

const formatDisplayDate = (value) => {
  if (!value) return '-';
  const cleanValue = value.replace(/[^0-9]/g, '');
  if (cleanValue.length === 8) {
    return `${cleanValue.slice(0, 4)}-${cleanValue.slice(4, 6)}-${cleanValue.slice(6, 8)}`;
  }
  return value;
};

export default FamilyWorshipList;
